/**
 * `customCategories` — per-org user-extensible category buckets.
 *
 * Lets studios add their own categories to any dropdown that has
 * an extensible "scope":
 *
 *   expense       — Org Finance > Expenses + Income (kind=expense)
 *   income        — Org Finance > Income (kind=income)
 *   payment_in    — Project transaction (money from client)
 *   payment_out   — Project transaction (money studio spends)
 *
 * Each form merges built-in (hardcoded) categories with the
 * studio's customs and offers an "+ Add new category" affordance.
 *
 * Why these four (and NOT lead.source / appointment.status / etc.):
 * those fields are workflow ENUMS that drive analytics filters,
 * status pipelines, and rule logic — extending them silently would
 * break dashboards and reports. Categories here are pure label/
 * bucket choices for the studio's own bookkeeping.
 *
 * Mirror at `src/lib/customCategories.ts` on mobile (same shape,
 * same scopes, same Firestore path).
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';

export type CustomCategoryScope =
  | 'expense'
  | 'income'
  | 'payment_in'
  | 'payment_out';

export type CustomCategory = {
  id: string;
  orgId: string;
  scope: CustomCategoryScope;
  /** Display label — what shows in the picker. Trimmed at write. */
  label: string;
  /** Sort order within the scope. 0 = first. */
  sortOrder: number;
  /** Soft-delete flag (hidden from picker but kept for historic rows). */
  archived?: boolean;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  updatedAt?: { toMillis(): number } | null;
};

export type CreateCustomCategoryInput = {
  orgId: string;
  scope: CustomCategoryScope;
  label: string;
  sortOrder?: number;
  createdBy: string;
};

// ───── CRUD ─────────────────────────────────────────────────

export async function createCustomCategory(
  input: CreateCustomCategoryInput,
): Promise<string> {
  const label = input.label.trim();
  if (!label) throw new Error('Category label is required.');
  const ref = await addDoc(collection(db, 'customCategories'), {
    orgId: input.orgId,
    scope: input.scope,
    label,
    sortOrder: input.sortOrder ?? 0,
    archived: false,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCustomCategory(
  id: string,
  patch: { label?: string; sortOrder?: number; archived?: boolean },
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.label !== undefined) updates.label = patch.label.trim();
  if (patch.sortOrder !== undefined) updates.sortOrder = patch.sortOrder;
  if (patch.archived !== undefined) updates.archived = patch.archived;
  await updateDoc(doc(db, 'customCategories', id), updates);
}

export async function deleteCustomCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, 'customCategories', id));
}

// ───── Live read hook ───────────────────────────────────────

export type UseCustomCategoriesResult = {
  data: CustomCategory[];
  loading: boolean;
};

/** Live subscription to an org's custom categories for a scope.
 *  Filters archived out automatically; sorts by sortOrder asc then
 *  label asc. */
export function useCustomCategories(
  orgId: string | null | undefined,
  scope: CustomCategoryScope,
): UseCustomCategoriesResult {
  const [raw, setRaw] = useState<CustomCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setRaw([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'customCategories'),
        where('orgId', '==', orgId),
        where('scope', '==', scope),
      ),
      (snap) => {
        const rows: CustomCategory[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<CustomCategory, 'id'>),
        }));
        setRaw(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useCustomCategories] snapshot error:', err);
        setRaw([]);
        setLoading(false);
      },
    );
  }, [orgId, scope]);

  const data = useMemo(() => {
    return raw
      .filter((c) => !c.archived)
      .sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.label.localeCompare(b.label);
      });
  }, [raw]);

  return { data, loading };
}
